import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Legend,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import {
  Activity,
  AlertCircle,
  Award,
  CheckCircle2,
  ClipboardCheck,
  Globe,
  Lightbulb,
  Plus,
  RefreshCw,
  Swords,
  Target,
  Trash2,
  TrendingDown,
  TrendingUp,
  X,
  XCircle,
} from 'lucide-react';
import { api } from '../../api/client';
import EmptyState from '../../components/EmptyState';
import AIBadge from '../../components/AIBadge';
import InfoTip from '../../components/InfoTip';
import ErrorBanner from '../../components/ErrorBanner';
import { SkeletonList } from '../../components/Skeleton';
import { toast } from '../../lib/toast';
import { formatRelative } from '../../lib/format';

// Stable color palette for competitors (10 distinct hues — enough for the
// Pro-tier cap of 10 competitors per domain). Your domain is always brand-blue.
const COMP_COLORS = [
  '#ff6a35', // orange
  '#5ac915', // green
  '#f91880', // magenta
  '#ffd106', // yellow
  '#9333ea', // purple
  '#06b6d4', // cyan
  '#dc2626', // red
  '#0891b2', // teal-700
  '#a855f7', // violet
  '#22c55e', // emerald
];
const YOU_COLOR = '#d61f97';

const PRIORITY_TONE = {
  high: 'bg-rose-50 text-rose-700 border-rose-100',
  medium: 'bg-amber-50 text-amber-700 border-amber-100',
  low: 'bg-gray-50 text-gray-700 border-gray-100',
};

export default function CompeteTab({ domain }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const compQuery = useQuery({
    queryKey: ['competitor-comparison', domain._id],
    queryFn: () =>
      api
        .get(`/api/domains/${domain._id}/competitors/comparison`)
        .then((r) => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (body) =>
      api
        .post(`/api/domains/${domain._id}/competitors`, body)
        .then((r) => r.data.competitor),
    onSuccess: (competitor) => {
      qc.invalidateQueries({ queryKey: ['competitor-comparison', domain._id] });
      setAdding(false);
      toast.success(`Added ${competitor.competitor_domain}`);
    },
    onError: (err) => toast.error(err, 'Could not add competitor'),
  });

  const scanMutation = useMutation({
    mutationFn: (id) =>
      api
        .post(`/api/domains/${domain._id}/competitors/${id}/scan`)
        .then((r) => r.data.competitor),
    onSuccess: (competitor) => {
      qc.invalidateQueries({ queryKey: ['competitor-comparison', domain._id] });
      toast.success(`Scanned ${competitor.competitor_domain}`);
    },
    onError: (err) => toast.error(err, 'Scan failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/api/domains/${domain._id}/competitors/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['competitor-comparison', domain._id] });
      toast.success('Competitor removed');
    },
    onError: (err) => toast.error(err, 'Could not remove competitor'),
  });

  const data = compQuery.data;
  const competitors = data?.competitors || [];
  const ownAudit = data?.ownAudit;
  const scannedCompetitors = competitors.filter((c) => c.last_scan);
  const quota = data?.quota;
  const atCap = quota && quota.cap !== -1 && competitors.length >= quota.cap;

  return (
    <div className="space-y-6">
      <CompeteHero
        domain={domain}
        ownAudit={ownAudit}
        competitorCount={competitors.length}
        scannedCount={scannedCompetitors.length}
        quota={quota}
        atCap={atCap}
        onAdd={() => setAdding(true)}
      />

      {!ownAudit && (
        <div className="rounded-xl border border-amber-100 bg-amber-50 text-amber-800 text-sm p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 flex-none mt-0.5" />
          <div>
            <div className="font-semibold">No audit on file for your own domain.</div>
            <div className="mt-1">
              Run an{' '}
              <Link to="../audit" className="underline font-medium">
                audit
              </Link>{' '}
              first so the comparison has a baseline. Competitor scans still work without
              it, but recommendations and pillar comparisons need both sides.
            </div>
          </div>
        </div>
      )}

      {adding && (
        <AddCompetitorCard
          onCancel={() => setAdding(false)}
          onSubmit={(body) => createMutation.mutate(body)}
          pending={createMutation.isPending}
          error={createMutation.error?.response?.data?.error}
        />
      )}

      {compQuery.isLoading ? (
        <SkeletonList rows={3} />
      ) : competitors.length === 0 ? (
        <EmptyState
          icon={Swords}
          title="No competitors added yet"
          body="Track up to a handful of competitor domains. We crawl each one with the same audit engine that scores your own site, then surface the gaps in plain language."
          action={
            <button className="btn-primary" onClick={() => setAdding(true)}>
              <Plus className="w-4 h-4" /> Add competitor
            </button>
          }
        />
      ) : (
        <CompetitorsList
          domain={domain}
          competitors={competitors}
          ownAudit={ownAudit}
          scanMutation={scanMutation}
          deleteMutation={deleteMutation}
        />
      )}

      {scannedCompetitors.length > 0 && ownAudit && (
        <>
          <PillarRadar
            ownAudit={ownAudit}
            competitors={scannedCompetitors}
            pillarMax={data.pillarMax}
            pillarLabels={data.pillarLabels}
          />
          <SchemaMatrix ownAudit={ownAudit} competitors={scannedCompetitors} />
          {data.recommendations?.length > 0 && (
            <RecommendationsCard recs={data.recommendations} />
          )}
        </>
      )}
    </div>
  );
}

function CompeteHero({ domain, ownAudit, competitorCount, scannedCount, quota, atCap, onAdd }) {
  const capLabel =
    quota?.cap === -1 ? 'unlimited' : quota?.cap != null ? String(quota.cap) : null;
  const upgradeHint =
    atCap && quota?.tier && quota.tier !== 'pro' && quota.tier !== 'enterprise'
      ? `Upgrade to Pro to track up to 10 competitors per domain.`
      : null;
  return (
    <div className="relative rounded-2xl overflow-hidden bg-ink-gradient text-white p-6 sm:p-8 shadow-xl">
      <div className="absolute inset-0 opacity-30 bg-brand-radial" />
      <div className="absolute inset-0 bg-grid opacity-20" />
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-brand-600 blur-3xl opacity-30" />
      <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-wider text-white/60 mb-1 flex items-center gap-1.5">
            <Swords className="w-3 h-3" /> Competitive benchmarking
            {capLabel && (
              <span className="pill bg-white/10 text-white/90 text-[10px] uppercase tracking-wider ml-1">
                {competitorCount}/{capLabel}
              </span>
            )}
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold">
            {ownAudit
              ? `Your GEO score: ${ownAudit.avg_score}/100`
              : 'See who AI engines are citing instead'}
          </h2>
          <p className="text-sm text-white/70 mt-1 max-w-xl">
            {competitorCount > 0
              ? `${scannedCount}/${competitorCount} competitor${competitorCount === 1 ? '' : 's'} scanned. Add more or re-scan to keep your view fresh.`
              : 'Add competitor domains and we’ll run the same 7-pillar audit against each one, then show you exactly where you’re winning and where you’re losing.'}
          </p>
          {upgradeHint && (
            <p className="text-xs text-white/60 mt-2 italic">{upgradeHint}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onAdd}
            disabled={atCap}
            title={atCap ? upgradeHint || 'Competitor limit reached' : undefined}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition-colors ${
              atCap
                ? 'bg-white/30 text-white/70 cursor-not-allowed'
                : 'bg-white text-ink-900 hover:bg-white/90'
            }`}
          >
            <Plus className="w-4 h-4" /> {atCap ? 'Limit reached' : 'Add competitor'}
          </button>
        </div>
      </div>
    </div>
  );
}

function AddCompetitorCard({ onCancel, onSubmit, pending, error }) {
  const [form, setForm] = useState({ competitor_domain: '', displayName: '' });
  const submit = (e) => {
    e.preventDefault();
    onSubmit(form);
  };
  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Plus className="w-4 h-4" />
          </span>
          Add competitor
        </div>
        <button className="btn-icon" onClick={onCancel} title="Cancel">
          <X className="w-4 h-4" />
        </button>
      </div>
      <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-2">
          <label className="label">Competitor domain</label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
            <input
              className="input pl-10"
              placeholder="competitor.com"
              required
              value={form.competitor_domain}
              onChange={(e) =>
                setForm({ ...form, competitor_domain: e.target.value })
              }
            />
          </div>
          <p className="text-xs text-ink-400 mt-1.5">
            Just the hostname — we strip protocols and www.
          </p>
        </div>
        <div>
          <label className="label">Display name (optional)</label>
          <input
            className="input"
            placeholder="Acme Inc."
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
          />
        </div>
        {error && (
          <div className="md:col-span-3">
            <ErrorBanner body={error} />
          </div>
        )}
        <div className="md:col-span-3 flex gap-2 justify-end">
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button type="submit" className="btn-primary" disabled={pending}>
            <Plus className="w-4 h-4" /> {pending ? 'Adding…' : 'Add competitor'}
          </button>
        </div>
      </form>
    </div>
  );
}

function CompetitorsList({
  domain,
  competitors,
  ownAudit,
  scanMutation,
  deleteMutation,
}) {
  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Swords className="w-4 h-4" />
          </span>
          Competitors
        </div>
        <span className="text-xs text-ink-400">
          {competitors.length} tracked
        </span>
      </div>
      <ul className="divide-y divide-gray-50">
        {/* Your own domain pinned at the top as a reference row */}
        {ownAudit && (
          <li className="py-3 flex items-center justify-between gap-3 first:pt-0">
            <div className="flex items-center gap-3 min-w-0">
              <span
                className="flex-none h-10 w-10 rounded-xl text-white flex items-center justify-center font-bold text-sm"
                style={{ backgroundColor: YOU_COLOR }}
              >
                {ownAudit.avg_score ?? '—'}
              </span>
              <div className="min-w-0">
                <div className="font-medium text-ink-900 flex items-center gap-2">
                  {domain.displayName || domain.domain}
                  <span className="pill-brand">You</span>
                </div>
                <div className="text-xs text-ink-400 truncate">
                  {ownAudit.total_pages_scanned} pages · audited{' '}
                  {formatRelative(ownAudit.scannedAt)}
                </div>
              </div>
            </div>
            <Link to="../audit" className="btn-icon" title="Open audit">
              <ClipboardCheck className="w-4 h-4" />
            </Link>
          </li>
        )}
        {competitors.map((c, idx) => (
          <CompetitorRow
            key={c._id}
            competitor={c}
            ownAudit={ownAudit}
            color={COMP_COLORS[idx % COMP_COLORS.length]}
            onScan={() => scanMutation.mutate(c._id)}
            scanning={scanMutation.isPending && scanMutation.variables === c._id}
            scanError={
              scanMutation.isError && scanMutation.variables === c._id
                ? scanMutation.error?.response?.data?.error
                : null
            }
            onDelete={() => {
              if (window.confirm(`Remove ${c.competitor_domain}?`)) {
                deleteMutation.mutate(c._id);
              }
            }}
          />
        ))}
      </ul>
    </div>
  );
}

function CompetitorRow({
  competitor,
  ownAudit,
  color,
  onScan,
  scanning,
  scanError,
  onDelete,
}) {
  const scan = competitor.last_scan;
  const score = scan?.avg_score;
  const delta =
    score != null && ownAudit?.avg_score != null ? score - ownAudit.avg_score : null;

  return (
    <li className="py-3 flex items-center justify-between gap-3 first:pt-0 last:pb-0">
      <div className="flex items-center gap-3 min-w-0">
        <span
          className="flex-none h-10 w-10 rounded-xl text-white flex items-center justify-center font-bold text-sm"
          style={{ backgroundColor: score != null ? color : '#9ca3af' }}
        >
          {score != null ? score : '—'}
        </span>
        <div className="min-w-0">
          <div className="font-medium text-ink-900 truncate">
            {competitor.displayName || competitor.competitor_domain}
          </div>
          <div className="text-xs text-ink-400 truncate flex items-center gap-2">
            <Globe className="w-3 h-3" />
            {competitor.competitor_domain}
            {scan && (
              <>
                <span>·</span>
                {scan.total_pages_scanned} pages · {formatRelative(scan.scannedAt)}
              </>
            )}
            {competitor.scan_status === 'failed' && (
              <>
                <span>·</span>
                <span className="text-rose-600 inline-flex items-center gap-1">
                  <XCircle className="w-3 h-3" /> last scan failed
                </span>
              </>
            )}
          </div>
          {scanError && (
            <div className="text-xs text-rose-600 mt-1 flex items-start gap-1">
              <AlertCircle className="w-3 h-3 mt-0.5 flex-none" /> {scanError}
            </div>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-none">
        {delta != null && (
          <span
            className={`pill ${
              delta > 0
                ? 'bg-rose-50 text-rose-700'
                : delta < 0
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-gray-100 text-gray-700'
            }`}
            title="Competitor score minus your score"
          >
            {delta > 0 ? (
              <TrendingUp className="w-3 h-3" />
            ) : delta < 0 ? (
              <TrendingDown className="w-3 h-3" />
            ) : null}
            {delta > 0 ? '+' : ''}
            {delta}
          </span>
        )}
        <button
          className="btn-secondary"
          onClick={onScan}
          disabled={scanning}
          title="Run / re-run audit"
        >
          {scanning ? (
            <>
              <Activity className="w-4 h-4 animate-pulse" /> Scanning…
            </>
          ) : scan ? (
            <>
              <RefreshCw className="w-4 h-4" /> Re-scan
            </>
          ) : (
            <>
              <Target className="w-4 h-4" /> Scan
            </>
          )}
        </button>
        <button
          className="btn-icon hover:!text-rose-600 hover:!bg-rose-50"
          onClick={onDelete}
          title="Remove competitor"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </li>
  );
}

function PillarRadar({ ownAudit, competitors, pillarMax, pillarLabels }) {
  // Recharts radar wants one row per axis (pillar) with one key per series.
  // Normalize each value to a 0–100 scale so axes are comparable across pillars.
  const data = useMemo(() => {
    const keys = Object.keys(pillarMax || {});
    return keys.map((key) => {
      const row = { pillar: pillarLabels?.[key] || key };
      const max = pillarMax[key] || 1;
      const own = ownAudit?.pillars?.[key] ?? 0;
      row['You'] = Math.round((own / max) * 100);
      competitors.forEach((c) => {
        const v = c.last_scan?.pillars?.[key] ?? 0;
        const label = c.displayName || c.competitor_domain;
        row[label] = Math.round((v / max) * 100);
      });
      return row;
    });
  }, [ownAudit, competitors, pillarMax, pillarLabels]);

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Target className="w-4 h-4" />
          </span>
          Pillar comparison
        </div>
        <span className="text-xs text-ink-400">% of max per pillar</span>
      </div>
      <ResponsiveContainer width="100%" height={360}>
        <RadarChart data={data} outerRadius="75%">
          <PolarGrid stroke="#e5e7eb" />
          <PolarAngleAxis dataKey="pillar" tick={{ fontSize: 11, fill: '#5d5b9d' }} />
          <PolarRadiusAxis
            domain={[0, 100]}
            tick={{ fontSize: 10, fill: '#9ca3af' }}
          />
          <Radar
            name="You"
            dataKey="You"
            stroke={YOU_COLOR}
            fill={YOU_COLOR}
            fillOpacity={0.35}
            strokeWidth={2}
          />
          {competitors.map((c, i) => {
            const label = c.displayName || c.competitor_domain;
            const color = COMP_COLORS[i % COMP_COLORS.length];
            return (
              <Radar
                key={c._id}
                name={label}
                dataKey={label}
                stroke={color}
                fill={color}
                fillOpacity={0.15}
                strokeWidth={2}
              />
            );
          })}
          <Tooltip
            contentStyle={{
              borderRadius: 12,
              border: '1px solid #e5e7eb',
              boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
            }}
          />
          <Legend />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

function SchemaMatrix({ ownAudit, competitors }) {
  // Union of all schema types across user + competitors.
  const allTypes = useMemo(() => {
    const s = new Set([...(ownAudit?.schema_types || [])]);
    for (const c of competitors) {
      for (const t of c.last_scan?.schema_types || []) s.add(t);
    }
    return [...s].sort();
  }, [ownAudit, competitors]);

  if (allTypes.length === 0) {
    return (
      <div className="card">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Award className="w-4 h-4" />
            </span>
            Schema coverage
          </div>
        </div>
        <p className="text-sm text-ink-400">
          No JSON-LD schema detected on any site yet. Run a fresh audit on your own domain
          (and at least one competitor scan) to populate this.
        </p>
      </div>
    );
  }

  const cols = [
    { id: 'you', label: 'You', types: new Set(ownAudit?.schema_types || []), color: YOU_COLOR },
    ...competitors.map((c, i) => ({
      id: c._id,
      label: c.displayName || c.competitor_domain,
      types: new Set(c.last_scan?.schema_types || []),
      color: COMP_COLORS[i % COMP_COLORS.length],
    })),
  ];

  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Award className="w-4 h-4" />
          </span>
          Schema coverage
        </div>
        <span className="text-xs text-ink-400">{allTypes.length} types found</span>
      </div>
      <div className="overflow-x-auto scroll-soft">
        <table className="data-table">
          <thead>
            <tr>
              <th>Schema @type</th>
              {cols.map((c) => (
                <th key={c.id} className="text-center">
                  <span className="inline-flex items-center gap-1.5 justify-center">
                    <span
                      className="w-2 h-2 rounded-full"
                      style={{ backgroundColor: c.color }}
                    />
                    {c.label}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {allTypes.map((t) => (
              <tr key={t}>
                <td className="font-mono text-xs text-ink-800">{t}</td>
                {cols.map((c) => (
                  <td key={c.id} className="text-center">
                    {c.types.has(t) ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" />
                    ) : (
                      <span className="text-ink-400">—</span>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RecommendationsCard({ recs }) {
  return (
    <div className="card">
      <div className="section-header">
        <div className="section-title">
          <span className="section-icon">
            <Lightbulb className="w-4 h-4" />
          </span>
          Catch up to the competition
          <AIBadge label="Auto-prioritized" />
          <InfoTip
            title="Where these come from"
            body="We diff your audit scores against each scanned competitor pillar-by-pillar, then surface the actions that close the biggest gaps first. High-priority items are pillars where you're losing by 5+ points."
          />
        </div>
        <span className="text-xs text-ink-400">{recs.length} actions</span>
      </div>
      <ul className="space-y-2">
        {recs.map((r, i) => (
          <li
            key={i}
            className={`rounded-xl border p-3 flex items-start gap-3 ${PRIORITY_TONE[r.priority] || PRIORITY_TONE.medium}`}
          >
            <span className="flex-none mt-0.5 h-5 w-5 rounded-full bg-white text-current text-[10px] font-bold flex items-center justify-center border border-current/30">
              {i + 1}
            </span>
            <div className="min-w-0">
              <div className="text-sm font-medium">{r.text}</div>
              <div className="text-[10px] uppercase tracking-wider mt-0.5 opacity-70">
                {r.priority} · {r.type}
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
